import React from 'react';
import _ from 'lodash';
import { Box, Card, CardContent, Divider, Select, Stack, TextField, Button, MenuItem, InputLabel, FormControl, FormHelperText, Alert } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { formatDate } from '../../../../../../../services/functions';

import { submitInvoiceAction } from '../../../../../../../services/request';
import { AlertDialogContainer } from '../../../../../../../components/others/AlertDialogContainer';
import { Position } from './Position';
import { dec2fixed } from './recalculate';
import BaseDataForm from './BaseDataForm';
import { BottomBaseDataForm } from './BottomBaseDataForm';

export const InvoiceForMarginReceiptForm = ({ order, invoiceBaseData, handleClose, season, module, editData, submitCallback, manual }) => {
  const productsRef = React.useRef();

  const { loading, mutate } = useMutation(submitInvoiceAction);

  const { number, buyer, seller, receipts } = invoiceBaseData;

  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [today, setToday] = React.useState(new Date());
  const [receiptsProducts, setReceiptsProducts] = React.useState([]);

  const validationSchema = Yup.object({
    number: Yup.string().required('To pole nie może być puste'),
    receipts: Yup.array().min(1, 'Musisz wybrać paragon')
      .required(),
    buyer_name: Yup.string().required('To pole nie może być puste'),
    buyer_city: Yup.string().required('To pole nie może być puste'),
    buyer_postal_code: Yup.string().required('To pole nie może być puste'),
    buyer_street_address: Yup.string().required('To pole nie może być puste'),
    seller_city: Yup.string().required('To pole nie może być puste'),
    seller_postal_code: Yup.string().required('To pole nie może być puste'),
    seller_street_address: Yup.string().required('To pole nie może być puste'),
    seller_company_name: Yup.string().required('To pole nie może być puste'),
    issue_date: Yup.date().required('To pole nie może być puste')
      .nullable()
  });

  const handleSubmit = async values => {
    const productErrors = productsRef.current.getAllErrors();
    if (productErrors.length === 0) {
      setSeverity('error');
      setTitle('Formularz zawiera błędy');
      setMessage('');
      setShowAlert(true);

      return;
    }
    let hasErrors = false;
    _.map(productErrors, obj => {
      if (obj.errors.length > 0 || obj.requiredFields.length > 0) {
        hasErrors = true;
      }
    });
    if (hasErrors) {
      setSeverity('error');
      setTitle('Formularz zawiera błędy');
      setMessage('');
      setShowAlert(true);

      return;
    }

    values.products = productsRef.current.getProducts();
    values.season = season;
    values.module = module;
    if (!_.isEmpty(editData)) {
      values.buyer_id = editData.buyer;
      values.recipient_id = editData.recipient;
    }
    const { error: mutateError, payload } = await mutate(values, !_.isEmpty(editData) ? 'PUT' : 'POST', !_.isEmpty(editData) ? editData.id : undefined);
    if (!mutateError) {
      submitCallback(!_.isEmpty(editData) ? 'edit' : 'create', payload);
      formik.resetForm();
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Formularz zawiera błędy');
      setMessage(payload.message || '');
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    initialValues: {
      order: !manual ? order.id : null,
      type: 9,
      number: !_.isNil(number) ? number : !_.isNil(editData.number) ? editData.number : '',
      buyer_name: !_.isNil(buyer) ? !_.isNil(buyer.name) ? buyer.name : '' : !_.isEmpty(editData) ? editData.buyer_name : '',
      buyer_city: !_.isNil(buyer) ? !_.isNil(buyer.address__city) ? buyer.address__city : '' : !_.isEmpty(editData) ? editData.buyer_city : '',
      buyer_postal_code: !_.isNil(buyer) ? !_.isNil(buyer.address__postal_code) ? buyer.address__postal_code : '' : !_.isEmpty(editData) ? editData.buyer_postal_code : '',
      buyer_street_address: !_.isNil(buyer) ? !_.isNil(buyer.address__street) ? buyer.address__street : '' : !_.isEmpty(editData) ? editData.buyer_street_address : '',
      buyer_nip: !_.isNil(buyer) ? !_.isNil(buyer.nip) ? buyer.nip : '' : !_.isEmpty(editData) ? editData.buyer_nip : '',
      seller_name: !_.isNil(seller) ? !_.isNil(seller.name) ? seller.name : '' : !_.isEmpty(editData) ? editData.seller_name : '',
      seller_city: !_.isNil(seller) ? !_.isNil(seller.address__city) ? seller.address__city : '' : !_.isEmpty(editData) ? editData.seller_city : '',
      seller_postal_code: !_.isNil(seller) ? !_.isNil(seller.address__postal_code) ? seller.address__postal_code : '' : !_.isEmpty(editData) ? editData.seller_postal_code : '',
      seller_street_address: !_.isNil(seller) ? !_.isNil(seller.address__street_address) ? seller.address__street_address : '' : !_.isEmpty(editData) ? editData.seller_street_address : '',
      seller_company_name: !_.isNil(seller) ? !_.isNil(seller.company__name) ? seller.company__name : '' : !_.isEmpty(editData) ? editData.seller_company_name : '',
      seller_nip: !_.isNil(seller) ? !_.isNil(seller.nip) ? seller.nip : '' : !_.isEmpty(editData) ? editData.seller_nip : '',
      bank_name: !_.isNil(seller) ? !_.isNil(seller.company__bank_accounts__name) ? seller.company__bank_accounts__name : '' : !_.isEmpty(editData) ? editData.bank_name : '',
      account_number: !_.isNil(seller) ? !_.isNil(seller.company__bank_accounts__account_number) ? seller.company__bank_accounts__account_number : '' : !_.isEmpty(editData) ? editData.account_number : '',
      payment_method: !_.isEmpty(editData) ? editData.payment_method : 'przelew',
      remarks: !_.isEmpty(editData) ? editData.remarks : '',
      issue_date: !_.isEmpty(editData) ? editData.issue_date : formatDate(today),
      sale_date: !_.isEmpty(editData) ? editData.sale_date : formatDate(today),
      receipts: !_.isEmpty(editData) ? editData.receipts : [],
      include_recipient: !_.isEmpty(editData) ? editData.include_recipient : false,
      recipient_name: !_.isEmpty(editData) ? editData.recipient_name : '',
      recipient_city: !_.isEmpty(editData) ? editData.recipient_city : '',
      recipient_postal_code: !_.isEmpty(editData) ? editData.recipient_postal_code : '',
      recipient_street_address: !_.isEmpty(editData) ? editData.recipient_street_address : '',
      recipient_nip: !_.isEmpty(editData) ? editData.recipient_nip : ''
    },
    validationSchema,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  if (_.isNil(receipts)) {
    return null;
  }


  if (!_.isNil(receipts) && receipts.length === 0) {
    return (
      <Box sx={{ width: '60vw', maxHeight: '75vh', padding: '5px', overflow: 'auto' }}>
        <Alert severity="info">Brak paragonów</Alert>
      </Box>
    );
  }

  const constructReceiptsProduct = val => {
    const selectedReceipts = _.filter(receipts, obj => val.includes(obj.id));
    const receiptProducts = [];
    const finalProducts = [];
    _.map(selectedReceipts, obj => receiptProducts.push(...obj.products));
    const usedVatRates = _.uniq(_.map(receiptProducts, 'vat_rate'));
    _.map(usedVatRates, (rate, key) => {
      finalProducts.push({ id: finalProducts.length, name: `${order.event_name}${key}`, quantity: '1', unit: '', pkwiu: '', gross_price: '0', net_price: '0', net_value: '0', vat_rate: rate, vat_amount: '0', gross_value: '0' });
    });
    _.map(finalProducts, finalProduct => {
      _.map(receiptProducts, product => {
        if (product.vat_rate == finalProduct.vat_rate) {
          finalProduct.gross_price = dec2fixed(parseFloat(finalProduct.gross_price) + parseFloat(product.gross_price));
          finalProduct.net_price = dec2fixed(parseFloat(finalProduct.net_price) + parseFloat(product.net_price));
          finalProduct.net_value = dec2fixed(parseFloat(finalProduct.net_value) + parseFloat(product.net_value));
          finalProduct.gross_value = dec2fixed(parseFloat(finalProduct.gross_value) + parseFloat(product.gross_value));
          finalProduct.vat_amount = dec2fixed(parseFloat(finalProduct.vat_amount) + parseFloat(product.vat_amount));
        }
      });
    });
    setReceiptsProducts(finalProducts);
    formik.setFieldValue('remarks', `Uczestnicy obozu: ${selectedReceipts[0].participant_name}`);
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <Box sx={{ maxHeight: '75vh', padding: '5px', overflow: 'auto' }}>
        <Card sx={{ marginTop: '20px' }}>
          <CardContent>
            <FormControl fullWidth error={formik.errors.receipts !== undefined}>
              <InputLabel id="receipts-label">Paragony</InputLabel>
              <Select
                id="receipts"
                name="receipts"
                labelId="receipts-label"
                label="Paragony"
                multiple
                value={formik.values.receipts}
                onChange={e => {
                  formik.setFieldValue('receipts', e.target.value);
                  constructReceiptsProduct(e.target.value);
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250
                    }
                  }
                }}
              >
                {receipts.map(receipt => <MenuItem key={receipt.id} value={receipt.id}>{receipt.number}</MenuItem>)}
              </Select>
              <FormHelperText>{formik.errors.receipts}</FormHelperText>
            </FormControl>
          </CardContent>
        </Card>
        <Card sx={{ marginBottom: '20px', marginTop: '20px' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <TextField
              sx={{ width: '100%' }}
              id="number"
              name="number"
              label="Numer faktury"
              value={formik.values.number}
              onChange={formik.handleChange}
              error={formik.errors.number && formik.touched.number}
              helperText={formik.errors.number}
            ></TextField>
          </CardContent>
        </Card>
        <BaseDataForm formik={formik} manual={manual} type={9} />
        <Divider sx={{ mt: 1 }} />
        <Stack sx={{ mt: 1, mb: 1 }}>
          <Position order={order} ref={productsRef} formik={formik} receiptsProducts={receiptsProducts} editData={editData} manual={manual} />
        </Stack>
        <BottomBaseDataForm formik={formik} manual={manual} order={order} type={9} />
      </Box>
      <Stack sx={{ marginTop: '20px' }} flexDirection="row" justifyContent="space-around">
        <Button sx={{ flexGrow: 0.35 }} type="submit" variant="outlined" onClick={handleClose}>anuluj</Button>
        <Button sx={{ flexGrow: 0.35 }} type="submit" variant="contained" disabled={loading}>{!_.isEmpty(editData) ? 'edytuj' : 'dodaj'}</Button>
      </Stack>
    </form>
  );
};
